<template>
  <div class="mt-2 px-6 employee-list-container">
    <LibrariesSkeletonLoader
      class="ml-6 mr-6"
      :is-loading="libraryLoading"
      is-requisition
    />

    <div
      v-if="!libraryLoading"
    >
      <NoDataBlock
        v-if="showNoDataBlock"
        label="Employee"
        document-type="Acknowledgements"
      >
        <template #description>
          There are no employees available for your selection.
        </template>
      </NoDataBlock>
      <template v-else>
        <StickyListHeader
          document-class="employee-acknowledgements"
          :header-columns="headerColumns"
          :selected-sort="selectedSort"
          :sort-direction="sortDirection"
          :select-all="allSelected"
          :select-all-disabled="!employeesSelectableForCurrentMode.length"
          top-offset="304px"
          @submit-sort="submitSort"
          @update:select-all="handleSelectAll"
        />
        <ListFilterEmptyState
          v-if="showEmptyState"
        />
        <LibraryList v-else>
          <LibraryListItemWrapper
            v-for="employeeWithUi in employeeAcknowledgementsWithUi"
            :key="employeeWithUi.ulid"
            @click="openEmployeePreview(employeeWithUi.ulid)"
          >
            <EmployeeAcknowledgementListItem
              class="employee-list-item"
              :ulid="employeeWithUi.ulid"
              :first-name="employeeWithUi.first_name"
              :last-name="employeeWithUi.last_name"
              :email="employeeWithUi.email"
              :selected="selectedEmployeeAcknowledgementIds.has(employeeWithUi.ulid)"
              :job-profile-title="employeeWithUi.job_profile_title || ''"
              :job-profile-ulid="employeeWithUi.job_profile_ulid || ''"
              :acknowledgement-status-key="employeeWithUi.acknowledgement_alert"
              :acknowledgement-status="getCurrentAcknowledgementLabel(employeeWithUi)"
              :days-pending="employeeWithUi.days_pending"
              :days-since-last-acknowledgement="getDaysSinceLastSigned(employeeWithUi)"
              :is-inactive="isEmployeeInactive(employeeWithUi)"
              :checkbox-disabled="isCheckboxDisabled(employeeWithUi)"
              :checkbox-disabled-reason="getCheckboxDisabledReason(employeeWithUi)"
              :employee-acknowledgement-row="employeeWithUi"
              :action-variant="getActionVariant(employeeWithUi)"
              :pending-notification-failed="Boolean(employeeWithUi.pending_notification_failed)"
              @update:selected="(value) => toggleSelectedEmployee(employeeWithUi.ulid, value)"
              @send-reminder="handleSendReminder"
              @create="handleCreateAction"
            />
          </LibraryListItemWrapper>
        </LibraryList>

        <StickyPagination
          v-if="totalPages > 1"
          :model-value="page"
          :length="totalPages"
          @update:model-value="(value) => sendFilterRequest({
            pageNumber: value,
            router,
          })"
        />
      </template>
    </div>

    <!-- Modals -->
    <CreateAcknowledgementModal
      :show="show.employeeAcknowledgementCreate"
      :employee-name="createModalEmployeeName"
      :employee-email="createModalEmployeeEmail"
      :job-profile="createModalJobProfile"
      :loading="createModalLoading"
      @close="closeCreateModal"
      @create="() => submitCreateAcknowledgement(getRefreshParams())"
    />
    <SendReminderModal
      :show="show.employeeAcknowledgementSendReminder"
      :employee-name="selectedEmployeeAcknowledgement ? `${selectedEmployeeAcknowledgement.first_name} ${selectedEmployeeAcknowledgement.last_name || ''}` : ''"
      :employee-email="selectedEmployeeAcknowledgement?.email || ''"
      :job-profile="selectedEmployeeAcknowledgement?.job_profile_title || ''"
      :loading="sendReminderLoading"
      @close="closeSendReminderModal"
      @send-reminder="() => submitSendReminder(getRefreshParams())"
    />
    <BulkSendReminderModal
      :show="show.employeeAcknowledgementBulkSendReminder"
      :employees="selectedEmployeesForBulkCreate"
      :loading="bulkReminderLoading"
      :results="bulkSendReminderModalResults"
      @close="handleBulkSendReminderModalClose"
      @send-reminder="submitBulkSendReminder"
    />
    <CancelAcknowledgementModal
      :show="show.employeeAcknowledgementCancel"
      :employee-name="selectedEmployeeAcknowledgement ? `${selectedEmployeeAcknowledgement.first_name} ${selectedEmployeeAcknowledgement.last_name || ''}` : ''"
      :employee-email="selectedEmployeeAcknowledgement?.email || ''"
      :job-profile="selectedEmployeeAcknowledgement?.job_profile_title || ''"
      :loading="cancelLoading"
      @close="closeCancelModal"
      @cancel="() => submitCancel(getRefreshParams())"
    />
    <BulkCreateAcknowledgementModal
      :show="show.employeeAcknowledgementBulkCreate"
      :employees="selectedEmployeesForBulkCreate"
      :loading="bulkCreateModalLoading"
      :results="bulkCreateModalResults"
      @close="handleBulkCreateModalClose"
      @create="submitBulkCreateAcknowledgement"
    />
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import LibrariesSkeletonLoader
  from '@/components/shared/SkeletonLoaders/LibrariesSkeletonLoader.vue';
import { useAppLoading } from '@/stores/app/loading';
import {
  computed, onMounted, onBeforeUnmount,
} from 'vue';
import LibraryListItemWrapper from '@/components/shared/LibraryListItemWrapper.vue';
import LibraryList from '@/components/shared/LibraryList.vue';
import { useEmployeeAcknowledgementsStore } from '@/stores/jobProfiles/employeeAcknowledgements';
import { useAcknowledgementsConfigStore } from '@/stores/jobProfiles/acknowledgementsConfig';
import EmployeeAcknowledgementListItem from '@/components/library/employeeAcknowledgements/EmployeeAcknowledgementListItem.vue';
import ListFilterEmptyState from '@/components/shared/ListFilterEmptyState.vue';
import NoDataBlock from '@/components/library/NoDataBlock.vue';
import StickyListHeader from '@/components/library/StickyListHeader.vue';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useFilters } from '@/stores/filters/filters';
import { useFilterDrawer } from '@/stores/filters/filterDrawer';
import { useModals } from '@/stores/modals/modals';
import { useAppBase } from '@/stores/app/base';
import { useRoute, useRouter } from 'vue-router';
import StickyPagination from '@/components/shared/StickyPagination.vue';
import { useAppPagination } from '@/stores/app/pagination';
import { useSubMenu } from '@/stores/filters/subMenu';
import { stickyHeadersEmployees } from '@/helpers/constants/stickyHeaders';
import { tooltipKeys } from '@/helpers/constants/tooltipKeys';
import { useTooltips } from '@/stores/insights/tooltips';
import { getTooltip } from '@/helpers/tooltips';
import CreateAcknowledgementModal from '@/components/jobProfiles/EmployeeAcknowledgementModals/CreateAcknowledgementModal.vue';
import BulkCreateAcknowledgementModal
  from '@/components/jobProfiles/EmployeeAcknowledgementModals/BulkCreateAcknowledgementModal.vue';
import SendReminderModal from '@/components/jobProfiles/EmployeeAcknowledgementModals/SendReminderModal.vue';
import BulkSendReminderModal
  from '@/components/jobProfiles/EmployeeAcknowledgementModals/BulkSendReminderModal.vue';
import CancelAcknowledgementModal from '@/components/jobProfiles/EmployeeAcknowledgementModals/CancelAcknowledgementModal.vue';
import { useTracking } from '@/stores/tracking/tracking';
import {
  getAcknowledgementAlertLabel,
  ACKNOWLEDGEMENT_ALERT_LABELS,
} from '@/helpers/acknowledgementAlerts';

const emit = defineEmits(['track-page-load']);

const EMPLOYEE_ACK_SORT_COLUMN_LABELS = {
  first_name: 'Employee',
  job_profile_title: 'Job Profile',
  acknowledgement_alert: 'Current Acknowledgement',
  days_since_last_signed: 'Days Since Last Signed',
};

const route = useRoute();
const router = useRouter();

const { pageLoading, listLoading } = storeToRefs(useAppLoading());
const { totalPages, page } = storeToRefs(useAppPagination());
const { selectedSubMenuOption } = storeToRefs(useSubMenu());
const { areFiltersApplied, mergedTempFilters } = storeToRefs(useFilters());
const { show } = storeToRefs(useModals());
const {
  employeeAcknowledgements,
  textFilter,
  createModalLoading,
  createModalEmployeeName,
  createModalEmployeeEmail,
  createModalJobProfile,
  selectedEmployeeAcknowledgement,
  sendReminderLoading,
  cancelLoading,
  selectedEmployeeAcknowledgementIds,
  bulkCreateModalLoading,
  bulkCreateModalResults,
  bulkSendReminderModalResults,
  bulkReminderLoading,
  selectedEmployeesForBulkCreate,
} = storeToRefs(useEmployeeAcknowledgementsStore());

const {
  setTextFilter,
  loadPage: loadEmployeeAcknowledgementsPage,
  resetIsActiveDefault,
  resetSelectedAcknowledgementStatus,
  clearSelectedEmployees,
  setSelectedEmployees,
  toggleSelectedEmployee,
  openCreateModal,
  closeCreateModal,
  submitCreateAcknowledgement,
  closeBulkCreateModal,
  submitBulkCreateAcknowledgement,
  openSendReminderModal,
  closeSendReminderModal,
  closeBulkSendReminderModal,
  submitBulkSendReminder,
  closeCancelModal,
  submitSendReminder,
  submitCancel,
} = useEmployeeAcknowledgementsStore();

const { setFilterConfig, submitSubMenu, sendFilterRequest } = useFilters();
const { open: filterDrawerOpen } = storeToRefs(useFilterDrawer());
const { showFilterBarSettings } = useAppBase();
const { windowWidth } = storeToRefs(useAppBase());

const FILTER_DRAWER_OPEN_HIDE_HEADER_TOOLTIP_MAX_WIDTH = 1600;
const { loadTooltips } = useTooltips();
const { loadConfig: loadAcknowledgementsConfig } = useAcknowledgementsConfigStore();

const libraryLoading = computed(() => pageLoading.value || listLoading.value);

const hasFiltersOrSearch = computed(() => {
  const hasSearch = Boolean(route.query?.q);

  return hasSearch || areFiltersApplied.value;
});

const showNoDataBlock = computed(() => (
  !libraryLoading.value
  && employeeAcknowledgements.value.length === 0
  && !hasFiltersOrSearch.value
));

const showEmptyState = computed(() => (
  !libraryLoading.value
  && employeeAcknowledgements.value.length === 0
  && hasFiltersOrSearch.value
));

const loadPage = (params) => {
  const searchTerm = textFilter.value || params.searchTerm;

  // When called from filter update (emitter), payload has no routeQuery; build from payload
  // so cleared filters are reflected in the URL. When called from mount, use route.query.
  const routeQuery = params.routeQuery || {
    ...params.selectedFilters,
    ...(params.page && { page: params.page }),
    ...(params.selectedMyJobs && { owned: params.selectedMyJobs }),
    ...(params.searchTerm && { q: params.searchTerm }),
    ...(params.selectedSubMenuOption && { sort: params.selectedSubMenuOption }),
  };

  const paramsWithSearch = {
    ...params,
    routeQuery,
    searchTerm,
  };

  loadEmployeeAcknowledgementsPage(paramsWithSearch);
};

const getRefreshParams = () => ({
  routeQuery: route.query,
  selectedFilters: mergedTempFilters.value,
  page: route.query?.page || 1,
  searchTerm: textFilter.value,
  router,
});

const handleBulkSendReminderModalClose = () => {
  closeBulkSendReminderModal();

  clearSelectedEmployees();

  loadEmployeeAcknowledgementsPage(getRefreshParams());
};

const handleBulkCreateModalClose = () => {
  closeBulkCreateModal();

  clearSelectedEmployees();

  loadEmployeeAcknowledgementsPage(getRefreshParams());
};

const selectedSort = computed(() => {
  const fallback = route.query?.sort || 'first_name';
  return (selectedSubMenuOption.value || fallback).replace('-', '');
});

const sortDirection = computed(() => (
  (selectedSubMenuOption.value || route.query?.sort || 'first_name').startsWith('-')
    ? 'desc'
    : 'asc'
));

const headerColumns = computed(() => {
  const baseColumns = [
    stickyHeadersEmployees.select,
    stickyHeadersEmployees.employee,
    stickyHeadersEmployees.jobProfile,
    stickyHeadersEmployees.acknowledgementStatus,
    stickyHeadersEmployees.daysSinceLastAcknowledgement,
    stickyHeadersEmployees.action,
  ];

  const hideHeaderTooltips = filterDrawerOpen.value
    && windowWidth.value < FILTER_DRAWER_OPEN_HIDE_HEADER_TOOLTIP_MAX_WIDTH;

  return baseColumns.map((column) => {
    if (hideHeaderTooltips) {
      return { ...column, tooltip: null };
    }

    if (column.tooltip?.key) {
      const tooltip = getTooltip(column.tooltip.key);

      return {
        ...column,
        tooltip: tooltip.html ? tooltip : null,
      };
    }

    return column;
  });
});

const getCurrentAcknowledgementLabel = (employee) => {
  if (!employee) return '—';

  const status = employee.acknowledgement_alert;

  if (!status) return '—';
  if (status === 'inactive') return 'Inactive Employee';

  return getAcknowledgementAlertLabel(status);
};

const getDaysSinceLastSigned = (employee) => {
  if (!employee
    || !employee.is_active
    || employee.acknowledgement_alert === 'inactive'
    || employee.acknowledgement_alert === 'no_acknowledgement'
  ) return null;

  return employee.days_since_last_signed;
};

const isEmployeeInactive = (employeeAcknowledgement) => !employeeAcknowledgement?.is_active;

const hasJobProfile = (row) => Boolean(row?.job_profile_ulid || row?.job_profile_title);

const isBaseSelectable = (row) => Boolean(row?.is_active) && Boolean(row?.email) && hasJobProfile(row);

const isPendingStatus = (status) => status === 'pending_acknowledgement';

const isJobProfileChangedAlert = (status) => (
  status === 'job_profile_id_changed' || status === 'job_profile_content_changed'
);

const isJobProfileChangedWithPending = (row) => (
  isJobProfileChangedAlert(row?.acknowledgement_alert)
  && typeof row?.days_pending === 'number'
);

const isEffectivelyPending = (row) => (
  isPendingStatus(row?.acknowledgement_alert) || isJobProfileChangedWithPending(row)
);

const hasSelectedPending = computed(() => {
  const ids = selectedEmployeeAcknowledgementIds.value;
  const list = employeeAcknowledgements.value;

  if (!ids?.size || !list?.length) return false;

  return list.some((row) => ids.has(row.ulid) && isEffectivelyPending(row));
});

const hasSelectedNonPending = computed(() => {
  const ids = selectedEmployeeAcknowledgementIds.value;
  const list = employeeAcknowledgements.value;

  if (!ids?.size || !list?.length) return false;

  return list.some((row) => ids.has(row.ulid) && isBaseSelectable(row) && !isEffectivelyPending(row));
});

const isCheckboxDisabled = (row) => {
  if (!isBaseSelectable(row)) return true;

  if (hasSelectedPending.value && !isEffectivelyPending(row)) return true;
  if (hasSelectedNonPending.value && isEffectivelyPending(row)) return true;

  return false;
};

const getCheckboxDisabledReason = (row) => {
  if (!row?.is_active) {
    return `New acknowledgements cannot be created for inactive employees. 
      Click on the employee's row to view their previous acknowledgements.`;
  }

  if (!row?.email) {
    return `Acknowledgements can only be sent to employees with an e-mail address. 
      Please update your employee's e-mail address in your HRIS.`;
  }

  if (!hasJobProfile(row)) {
    return `Please assign a job profile to this employee in your 
      HRIS in order to create an acknowledgement.`;
  }

  if (hasSelectedPending.value && !isEffectivelyPending(row)) {
    return `Employees with other statuses cannot be selected while employees with status 
      "${ACKNOWLEDGEMENT_ALERT_LABELS.pending_acknowledgement}" are already selected.`;
  }

  if (hasSelectedNonPending.value && isEffectivelyPending(row)) {
    return `Employees with status "${ACKNOWLEDGEMENT_ALERT_LABELS.pending_acknowledgement}" cannot be selected while employees 
      of another status are already selected.`;
  }

  return null;
};

const getActionVariant = (row) => {
  if (isEmployeeInactive(row) || row.acknowledgement_alert === 'inactive') {
    return 'none';
  }

  if (isPendingStatus(row.acknowledgement_alert)) return 'pending';

  if (isJobProfileChangedWithPending(row)) return 'cancel-only';

  if (!row?.email || !hasJobProfile(row)) {
    return 'none';
  }

  return 'create';
};

const employeeAcknowledgementsWithUi = computed(() => [...employeeAcknowledgements.value]);

const currentPageHasOnlyPending = computed(() => {
  const list = employeeAcknowledgements.value;

  return Boolean(list?.length) && list.every(
    (row) => isEffectivelyPending(row),
  );
});

const employeesSelectableForCurrentMode = computed(() => {
  const list = employeeAcknowledgements.value;

  if (!list?.length) return [];

  if (hasSelectedPending.value) {
    return list.filter(
      (row) => isBaseSelectable(row) && isEffectivelyPending(row),
    );
  }

  if (currentPageHasOnlyPending.value) {
    return list.filter(
      (row) => isBaseSelectable(row) && isEffectivelyPending(row),
    );
  }

  return list.filter(
    (row) => isBaseSelectable(row) && !isEffectivelyPending(row),
  );
});

const handleCreateAction = (employeeAcknowledgement) => {
  useTracking().ackLibClickedCreate({ location: 'On Individual Employee Row' });

  const variant = employeeAcknowledgement.acknowledgement_alert === 'acknowledgement_ok' ? 'new' : 'existing';

  openCreateModal(employeeAcknowledgement, variant);
};

const handleSendReminder = (employeeAcknowledgement) => {
  useTracking().ackLibClickedRemind({ location: 'On Individual Employee Row' });

  openSendReminderModal(employeeAcknowledgement);
};

const openEmployeePreview = (employeeUlid) => {
  useTracking().ackLibOpenedEmployeePreview();

  router.replace({
    query: { ...route.query, preview: employeeUlid, type: 'employee' },
  });
};

const allSelected = computed(() => {
  const target = employeesSelectableForCurrentMode.value;

  return target.length > 0
    && target.every(({ ulid }) => selectedEmployeeAcknowledgementIds.value.has(ulid));
});

const handleSelectAll = (value) => {
  if (!value) {
    clearSelectedEmployees();

    return;
  }

  setSelectedEmployees(
    employeesSelectableForCurrentMode.value.map(({ ulid }) => ulid),
  );
};

const submitSort = (selectedId) => {
  const current = selectedSubMenuOption.value || route.query?.sort || 'first_name';
  const currentBase = current.startsWith('-') ? current.slice(1) : current;

  if (selectedId === '__select') return;

  let optionId;
  if (selectedId === currentBase) {
    optionId = current.startsWith('-') ? selectedId : `-${selectedId}`;
  } else {
    optionId = selectedId;
  }

  const isDescending = optionId.startsWith('-');
  const direction = isDescending ? 'desc' : 'asc';
  const baseSortField = isDescending ? optionId.slice(1) : optionId;
  const column = EMPLOYEE_ACK_SORT_COLUMN_LABELS[baseSortField] || baseSortField;

  useTracking().ackLibSortedTable({ column, direction });

  submitSubMenu({ optionId, router });
};

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  setFilterConfig({
    module: 'employeeAcknowledgements',
    endpoint: 'employeeAcknowledgementsLibrary',
    showMyJobs: false,
    datepicker: false,
    ignorableFilters: ['acknowledgement_alert'],
  });

  showFilterBarSettings({ route: route.name });

  const searchTerm = route.query?.q;

  if (searchTerm) setTextFilter(searchTerm);

  await loadTooltips(tooltipKeys['employee-acknowledgements']);

  loadAcknowledgementsConfig();

  loadPage({
    routeQuery: route.query || '',
    router,
    page: route.query?.page || 1,
  });

  emit('track-page-load', { page: 'Employee Acknowledgements: Open' });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);
  setTextFilter('');
  resetIsActiveDefault();
  resetSelectedAcknowledgementStatus(router);
  clearSelectedEmployees();

  setFilterConfig({
    subMenuType: null,
    ignorableFilters: [],
  });
});
</script>

<style lang="less" scoped>
:deep(.sticky-header-items) {
  height: 100%;
}

:deep(.document-list-item-wrapper):not(:last-child) {
  border-bottom: 1px solid @stroke-default;
}

.employee-list-item, :deep(.sticky-header-items.employee-acknowledgements) {
  grid-template-columns: 52px minmax(80px, 1fr) minmax(210px, 1fr) minmax(123px, 191px) minmax(70px, 200px) 100px;
  height: 100%;
  gap: 0;
}

:deep(.sticky-header-items.employee-acknowledgements) {
  .select {
    justify-self: start;
    padding-left: 24px;

    .v-checkbox-btn {
      .v-selection-control__input {
        width: 20px;
        height: 20px;

        svg {
          width: 20px;
          height: 20px;
        }
      }

      .v-checkbox-btn__overlay {
        width: 20px;
        height: 20px;
      }
    }
  }

  .employee,
  .job-profile {
    justify-self: start;
    padding-left: 16px;
  }

  .acknowledgement-status {
    justify-self: start;
    padding-left: 16px;
    text-align: left;
  }

  .days-since {
    justify-self: center;
    padding-left: 0;
    text-align: center;
  }

  .action {
    justify-self: center;
    padding-left: 0;
    padding-right: 0;
  }
}

:deep(.sticky-header-items.employee-acknowledgements .sortable-column) {
  width: 100%;

  p {
    font-size: 12px !important;
    font-weight: 500 !important;
    line-height: 150% !important;
  }

  &.acknowledgement-status {
    justify-content: flex-start;
  }

  &.days-since {
    justify-content: center;
  }

  &.action {
    justify-content: center;
  }
}

</style>
